import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';
import { trackUnitAdded, trackUnitConversionSet, trackSupplierLinkedToUnit } from '../services/analytics';
import { Toast, Modal, Field, PageHeader, ProductSearch, BtnPrimary, BtnSecondary } from './v2-utils';
import '../styles/v2.css';

const DRAFT_KEY    = 'add_unit_draft';
const SUPPLIER_KEY = 'last_created_supplier_id_add_unit';

function formatProductDisplay(p) {
  let s = p.product_name;
  if (p.brand)   s += ` (${p.brand || 'No Brand'})`;
  if (p.variety) s += ` - ${p.variety}`;
  return s;
}

function ProfitBox({ buyUnit, sellUnit, orderPrice, retailPrice, convRate, isAddingNew, unitCategory, newUnitType, compUnit }) {
  const retail = parseFloat(retailPrice), order = parseFloat(orderPrice), conv = parseFloat(convRate);
  if (!retail || !order || !conv || conv === 0) return null;
  const costPerSell = order / conv;
  const margin = (retail - costPerSell) / costPerSell;
  const isWarn = margin < 0 || margin > 1;
  return (
    <div style={{ padding:'16px', borderRadius:12, background:isWarn?'#fffbeb':'#eff6ff', border:`1px solid ${isWarn?'#fde68a':'#bfdbfe'}` }}>
      <p style={{ margin:'0 0 8px', fontSize:13, fontWeight:700, color:isWarn?'#92400e':'#1d4ed8' }}>Profit Margin Calculation</p>
      <p style={{ margin:'2px 0', fontSize:12, color:'#475569' }}>Cost per {isAddingNew ? (compUnit||'comparison unit') : (sellUnit||'selling unit')}: K {costPerSell.toFixed(2)}</p>
      <p style={{ margin:'2px 0', fontSize:12, color:'#475569' }}>Profit: K {(retail - costPerSell).toFixed(2)}</p>
      <p style={{ margin:'6px 0 0', fontSize:13, fontWeight:700, color:isWarn?'#92400e':'#1d4ed8' }}>Margin: {(margin*100).toFixed(1)}%</p>
      {margin < 0 && <p style={{ margin:'4px 0 0', fontSize:12, color:'#ef4444', fontWeight:600 }}>⚠️ This results in a loss</p>}
      {margin > 1 && <p style={{ margin:'4px 0 0', fontSize:12, color:'#f59e0b', fontWeight:600 }}>⚠️ Margin seems unusually high</p>}
    </div>
  );
}

function SupplierSection({ label, suppliers, selectedSupplierIds, setSelectedSupplierIds, primarySupplierId, setPrimarySupplierId, setAddSupplierOpen }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      <p style={{ fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.08em', margin:0 }}>{label}</p>
      <div style={{ border:'1.5px solid #e2e8f0', borderRadius:12, overflow:'hidden' }}>
        {suppliers.map(s=>(
          <label key={s.supplier_id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 16px', borderBottom:'1px solid #f1f5f1', cursor:'pointer', background:selectedSupplierIds.includes(String(s.supplier_id))?'#f0fdf4':'white' }}>
            <input type="checkbox" checked={selectedSupplierIds.includes(String(s.supplier_id))} onChange={e=>{ const id=String(s.supplier_id); setSelectedSupplierIds(prev=>e.target.checked?[...prev,id]:prev.filter(x=>x!==id)); if(!e.target.checked&&primarySupplierId===id) setPrimarySupplierId(''); }} style={{ accentColor:'#10b981', width:16, height:16 }} />
            <span style={{ fontSize:13, fontWeight:500, color:'#334155', flex:1 }}>{s.name||s.supplier_name||'Unnamed'}</span>
            {selectedSupplierIds.includes(String(s.supplier_id)) && (
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#065f46', cursor:'pointer' }}>
                <input type="radio" name="primary_supplier" value={String(s.supplier_id)} checked={primarySupplierId===String(s.supplier_id)} onChange={()=>setPrimarySupplierId(String(s.supplier_id))} style={{ accentColor:'#10b981' }} />
                Primary
              </label>
            )}
          </label>
        ))}
        <button type="button" onClick={()=>setAddSupplierOpen(true)} style={{ display:'flex', alignItems:'center', gap:8, width:'100%', padding:'10px 16px', background:'none', border:'none', cursor:'pointer', fontSize:13, color:'#10b981', fontWeight:600 }}>
          <svg style={{ width:14,height:14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>Add New Supplier
        </button>
      </div>
    </div>
  );
}

function UnitTypeInput({ label, value, onChange, error, hint, unitTypeError, setUnitTypeError, searchUnitTypes, inputId }) {
  const listId = inputId || 'unit-suggestions';
  return (
    <Field label={label} error={error?unitTypeError:''} hint={hint}>
      <input className="v2-input" type="text" placeholder="e.g. Crate, Bottle, Kg…" value={value} onChange={e=>{ onChange(e.target.value); if(unitTypeError) setUnitTypeError(''); }} list={listId} style={{ borderColor:error&&unitTypeError?'#ef4444':'' }} />
      <datalist id={listId}>{searchUnitTypes.map(t=><option key={t} value={t}/>)}</datalist>
    </Field>
  );
}

export default function AddUnitV2() {
  const navigate = useNavigate();
  const location = useLocation();
  const params   = new URLSearchParams(location.search);
  const fromProductFlow = params.get('from_product') === 'true';

  // product
  const [productId, setProductId]     = useState('');
  const [productName, setProductName] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isOnline, setIsOnline]       = useState(navigator.onLine);

  // mode
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [existingUnits, setExistingUnits] = useState([]);
  const [searchUnitTypes, setSearchUnitTypes] = useState([]);

  // first-time fields
  const [buyUnit, setBuyUnit]         = useState('');
  const [sellUnit, setSellUnit]       = useState('');

  // add-new fields
  const [unitCategory, setUnitCategory] = useState('buying');
  const [newUnitType, setNewUnitType]   = useState('');
  const [selectedExisting, setSelectedExisting] = useState('');

  // shared
  const [convRate, setConvRate]       = useState('');
  const [orderPrice, setOrderPrice]   = useState('');
  const [retailPrice, setRetailPrice] = useState('');

  // suppliers
  const [suppliers, setSuppliers]             = useState([]);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState([]);
  const [primarySupplierId, setPrimarySupplierId] = useState('');

  // ui
  const [toast, setToast]                 = useState(null);
  const [disabled, setDisabled]           = useState(false);
  const [profitWarnOpen, setProfitWarnOpen] = useState(false);
  const [pendingData, setPendingData]     = useState(null);
  const [addAnotherOpen, setAddAnotherOpen] = useState(false);
  const [addSupplierOpen, setAddSupplierOpen] = useState(false);
  const [unitTypeError, setUnitTypeError]   = useState('');

  const justSelectedRef = useRef(false);
  const blurTimeoutRef  = useRef(null);

  // online/offline
  useEffect(() => {
    const up=()=>setIsOnline(true), down=()=>setIsOnline(false);
    window.addEventListener('online',up); window.addEventListener('offline',down);
    const fetchSuppliers = async () => {
      try { const r=await axiosInstance.get('/suppliers'); setSuppliers((r.data||[]).filter(s=>!s.market_name)); } catch {}
    };
    fetchSuppliers();
    return () => { window.removeEventListener('online',up); window.removeEventListener('offline',down); if(blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current); };
  }, []);

  // restore draft from sessionStorage
  useEffect(() => {
    const json = sessionStorage.getItem(DRAFT_KEY);
    if (!json) return;
    try {
      const d = JSON.parse(json);
      setProductId(d.product_id||''); setProductName(d.productName||'');
      setBuyUnit(d.buyUnit||''); setSellUnit(d.sellUnit||'');
      setNewUnitType(d.newUnitType||''); setSelectedExisting(d.selectedExisting||'');
      setConvRate(d.convRate||''); setIsAddingNew(!!d.isAddingNew);
      setUnitCategory(d.unitCategory||'buying');
      setOrderPrice(d.orderPrice||''); setRetailPrice(d.retailPrice||'');
      const base = d.selectedSupplierIds||[];
      const newSup = sessionStorage.getItem(SUPPLIER_KEY);
      if (newSup) { const merged=base.includes(newSup)?base:[...base,newSup]; setSelectedSupplierIds(merged); setPrimarySupplierId(newSup); }
      else { setSelectedSupplierIds(base); setPrimarySupplierId(d.primarySupplierId||''); }
    } catch {}
    finally { sessionStorage.removeItem(DRAFT_KEY); sessionStorage.removeItem(SUPPLIER_KEY); }
  }, []);

  // product from URL
  useEffect(() => {
    const pid = params.get('product_id');
    if (!pid) return;
    axiosInstance.get(`/products/${pid}`).then(r => {
      setProductId(pid); setProductName(formatProductDisplay(r.data));
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // product search
  useEffect(() => {
    if (productName.length > 2 && isOnline && !justSelectedRef.current) {
      axiosInstance.get(`/products/search?q=${productName}`).then(res => {
        const unique = Object.values(res.data.reduce((acc,p)=>{const k=`${p.product_name}-${p.variety||''}-${p.brand||''}`; if(!acc[k])acc[k]=p; return acc;},{}));
        setSearchResults(unique);
      }).catch(()=>setSearchResults([]));
    } else setSearchResults([]);
    if (justSelectedRef.current) justSelectedRef.current = false;
  }, [productName, isOnline]);

  // fetch units when product changes
  useEffect(() => {
    if (!productId) { setExistingUnits([]); setSearchUnitTypes([]); return; }
    const fetch = async () => {
      try {
        const [prodR, unitsR] = await Promise.all([
          axiosInstance.get(`/products/${productId}`),
          axiosInstance.get(`/units/product/${productId}`),
        ]);
        const searchR = await axiosInstance.get(`/units/search/product/${encodeURIComponent(prodR.data.product_name)}`).catch(()=>({data:{unitTypes:[]}}));
        setSearchUnitTypes((searchR.data.unitTypes||[]).filter(t=>t&&t.trim()));
        setExistingUnits(unitsR.data||[]);
        setIsAddingNew((unitsR.data||[]).length > 0);
        const buying = (unitsR.data||[]).find(u=>u.unit_category==='buying'&&u.supplier_id);
        if (buying?.supplier_id) { setSelectedSupplierIds([String(buying.supplier_id)]); setPrimarySupplierId(String(buying.supplier_id)); }
      } catch {}
    };
    fetch();
  }, [productId]);

  const handleSelect = (p) => {
    if (blurTimeoutRef.current) { clearTimeout(blurTimeoutRef.current); blurTimeoutRef.current=null; }
    justSelectedRef.current = true;
    setProductId(String(p.product_id)); setProductName(formatProductDisplay(p)); setSearchResults([]);
    setBuyUnit(''); setSellUnit(''); setNewUnitType(''); setSelectedExisting(''); setConvRate(''); setOrderPrice(''); setRetailPrice(''); setSelectedSupplierIds([]); setPrimarySupplierId('');
  };

  const calcMargin = () => {
    const r=parseFloat(retailPrice), o=parseFloat(orderPrice), c=parseFloat(convRate);
    if (!r||!o||!c||c===0) return null;
    return (r - o/c)/(o/c);
  };

  const submitForm = async (data) => {
    setDisabled(true);
    try {
      await axiosInstance.post('/units', data);

      // persist suppliers
      if ((isAddingNew ? unitCategory==='buying' : true)) {
        const buyingTypeName = isAddingNew ? newUnitType : buyUnit;
        const unitsR = await axiosInstance.get(`/units/product/${productId}`);
        const target = (unitsR.data||[]).find(u=>u.unit_category==='buying'&&String(u.unit_type).toLowerCase()===buyingTypeName.toLowerCase());
        if (target && selectedSupplierIds.length > 0) {
          await axiosInstance.put(`/units/${target.unit_id}/suppliers`, { supplier_ids: selectedSupplierIds.map(Number) }).catch(()=>{});
          if (primarySupplierId) await axiosInstance.put(`/units/${target.unit_id}/primary-supplier`, { supplier_id: Number(primarySupplierId) }).catch(()=>{});
        }
      }

      trackUnitAdded(null, unitCategory);
      if (data.conversion_rate) trackUnitConversionSet(productId);
      if (selectedSupplierIds.length > 0) trackSupplierLinkedToUnit(null, primarySupplierId || selectedSupplierIds[0]);
      setToast({ message: 'Unit added successfully!', severity: 'success' });
      setUnitTypeError('');
      if (fromProductFlow) { setAddAnotherOpen(true); }
      else { resetForm(); }
    } catch (err) {
      if (err.response?.status === 409) {
        setUnitTypeError(err.response.data.error || 'Duplicate unit type — use a different name.');
        setToast({ message: 'Duplicate unit type.', severity: 'error' });
      } else {
        setToast({ message: err.response?.data?.error || 'Failed to add unit.', severity: 'error' });
      }
      setDisabled(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!productId) { setToast({ message: 'Please select a product.', severity: 'error' }); return; }
    const margin = calcMargin();
    let unitData;
    if (isAddingNew) {
      unitData = { product_id: productId, newUnitType, selectedExistingUnit: selectedExisting, conversion_rate: convRate, unitCategory, retail_price: retailPrice||'', order_price: orderPrice||'' };
    } else {
      unitData = { product_id: productId, buying_unit_type: buyUnit, selling_unit_type: sellUnit, conversion_rate: convRate, retail_price: retailPrice||'', order_price: orderPrice||'' };
    }
    if (margin !== null && (margin < 0 || margin > 1)) {
      setPendingData(unitData); setProfitWarnOpen(true); return;
    }
    await submitForm(unitData);
  };

  const resetForm = () => {
    setDisabled(false); setBuyUnit(''); setSellUnit(''); setNewUnitType(''); setSelectedExisting('');
    setConvRate(''); setOrderPrice(''); setRetailPrice(''); setUnitCategory('buying');
    setSelectedSupplierIds([]); setPrimarySupplierId(''); setUnitTypeError('');
    if (!fromProductFlow) { setProductId(''); setProductName(''); setIsAddingNew(false); }
  };

  const handleSaveAndAddSupplier = () => {
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ product_id:productId, productName, buyUnit, sellUnit, newUnitType, selectedExisting, convRate, isAddingNew, unitCategory, orderPrice, retailPrice, selectedSupplierIds, primarySupplierId })); }
    catch {}
    setAddSupplierOpen(false);
    navigate('/new/dashboard/suppliers/add', { state: { from: 'add_unit' } });
  };

  const compUnit = existingUnits.find(u=>u.unit_id===selectedExisting||String(u.unit_id)===String(selectedExisting));

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <PageHeader title={isAddingNew ? 'Add New Unit' : 'Add Unit'} subtitle="Define buying and selling units for a product"
        onBack={() => navigate('/new/dashboard/units/view')} />

      <form onSubmit={handleSubmit}>
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

          {/* Product search */}
          <div className="v2-card" style={{ padding:24 }}>
            <p style={{ fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.08em', margin:'0 0 16px' }}>Product</p>
            <Field label="Select Product" hint="Type 3+ characters to search">
              <ProductSearch value={productName} onChange={val=>{ setProductName(val); if(productId){setProductId('');setIsAddingNew(false);setExistingUnits([]);} }}
                onSelect={handleSelect}
                onBlur={()=>{ blurTimeoutRef.current=setTimeout(()=>setSearchResults([]),150); }}
                onFocus={()=>{ if(blurTimeoutRef.current){clearTimeout(blurTimeoutRef.current);blurTimeoutRef.current=null;} }}
                results={searchResults} />
            </Field>
          </div>

          {/* Form body — only shown after product selected */}
          {productId && (
            <>
              {!isAddingNew ? (
                /* ── First-time: create buying + selling ── */
                <div className="v2-card" style={{ padding:24, display:'flex', flexDirection:'column', gap:20 }}>

                  <div style={{ padding:'12px 16px', borderRadius:10, background:'#eff6ff', border:'1px solid #bfdbfe' }}>
                    <p style={{ margin:0, fontSize:13, color:'#1d4ed8', fontWeight:500 }}>This product has no units yet. Set up the buying and selling units together.</p>
                  </div>

                  <p style={{ fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.08em', margin:0 }}>Buying Information</p>
                  <SupplierSection label="Suppliers (optional)" suppliers={suppliers} selectedSupplierIds={selectedSupplierIds} setSelectedSupplierIds={setSelectedSupplierIds} primarySupplierId={primarySupplierId} setPrimarySupplierId={setPrimarySupplierId} setAddSupplierOpen={setAddSupplierOpen} />
                  <UnitTypeInput label="Buying Unit Type *" value={buyUnit} onChange={setBuyUnit} error={!!unitTypeError&&(!sellUnit||unitTypeError)} hint="e.g. Crate, Box, Sack" unitTypeError={unitTypeError} setUnitTypeError={setUnitTypeError} searchUnitTypes={searchUnitTypes} inputId="unit-suggestions-buy" />
                  <Field label="Order Price per Buying Unit (K)">
                    <input className="v2-input" type="number" placeholder="0.00" value={orderPrice} onChange={e=>setOrderPrice(e.target.value)} min="0" step="0.01" />
                  </Field>

                  <div style={{ height:1, background:'#f1f5f1' }} />
                  <p style={{ fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.08em', margin:0 }}>Selling Information</p>
                  <UnitTypeInput label="Selling Unit Type *" value={sellUnit} onChange={setSellUnit} error={false} hint="e.g. Bottle, Piece, Kg" unitTypeError={unitTypeError} setUnitTypeError={setUnitTypeError} searchUnitTypes={searchUnitTypes} inputId="unit-suggestions-sell" />
                  <Field label="Retail Price per Selling Unit (K)">
                    <input className="v2-input" type="number" placeholder="0.00" value={retailPrice} onChange={e=>setRetailPrice(e.target.value)} min="0" step="0.01" />
                  </Field>

                  <div style={{ height:1, background:'#f1f5f1' }} />
                  <Field label={`How many ${sellUnit||'selling units'} are in one ${buyUnit||'buying unit'}? *`} hint="Conversion rate">
                    <input className="v2-input" type="number" placeholder="e.g. 24" value={convRate} onChange={e=>setConvRate(e.target.value)} min="0" step="0.01" required />
                  </Field>

                  <ProfitBox buyUnit={buyUnit} sellUnit={sellUnit} orderPrice={orderPrice} retailPrice={retailPrice} convRate={convRate} isAddingNew={false} />
                </div>

              ) : (
                /* ── Add new unit to existing product ── */
                <div className="v2-card" style={{ padding:24, display:'flex', flexDirection:'column', gap:20 }}>

                  {/* Existing units summary */}
                  <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
                    {['buying','selling'].map(cat=>{
                      const items=existingUnits.filter(u=>u.unit_category===cat);
                      return (
                        <div key={cat} style={{ flex:1, minWidth:120, padding:'10px 14px', borderRadius:10, background:'#f8fafc', border:'1px solid #f1f5f1' }}>
                          <p style={{ margin:'0 0 4px', fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em' }}>{cat}</p>
                          <p style={{ margin:0, fontSize:13, fontWeight:600, color:'#334155' }}>{items.length>0?items.map(u=>u.unit_type).join(', '):'None'}</p>
                        </div>
                      );
                    })}
                  </div>

                  <Field label="New Unit Category *">
                    <div style={{ display:'flex', gap:10 }}>
                      {['buying','selling'].map(cat=>(
                        <button key={cat} type="button" onClick={()=>{ setUnitCategory(cat); if(cat!=='buying'){setSelectedSupplierIds([]);setPrimarySupplierId('');} }}
                          style={{ flex:1, padding:'10px 0', borderRadius:10, border:`1.5px solid ${unitCategory===cat?'#10b981':'#e2e8f0'}`, background:unitCategory===cat?'#f0fdf4':'white', color:unitCategory===cat?'#065f46':'#475569', fontSize:13, fontWeight:700, cursor:'pointer', textTransform:'capitalize', transition:'all 0.15s' }}>
                          {cat}
                        </button>
                      ))}
                    </div>
                  </Field>

                  {unitCategory==='buying' && <SupplierSection label="Suppliers (optional)" suppliers={suppliers} selectedSupplierIds={selectedSupplierIds} setSelectedSupplierIds={setSelectedSupplierIds} primarySupplierId={primarySupplierId} setPrimarySupplierId={setPrimarySupplierId} setAddSupplierOpen={setAddSupplierOpen} />}

                  <UnitTypeInput label="New Unit Type *" value={newUnitType} onChange={setNewUnitType} error={!!unitTypeError} hint="Type or search from existing unit names" unitTypeError={unitTypeError} setUnitTypeError={setUnitTypeError} searchUnitTypes={searchUnitTypes} inputId="unit-suggestions-new" />

                  <Field label="Price per New Unit (K)" hint={unitCategory==='buying'?'Order price':'Retail price'}>
                    <input className="v2-input" type="number" placeholder="0.00" value={unitCategory==='buying'?orderPrice:retailPrice}
                      onChange={e=>unitCategory==='buying'?setOrderPrice(e.target.value):setRetailPrice(e.target.value)} min="0" step="0.01" />
                  </Field>

                  <Field label="Compare to existing unit *" hint="Which existing unit does this relate to?">
                    <div style={{ position:'relative' }}>
                      <select className="v2-input" style={{ paddingRight:36, appearance:'none', cursor:'pointer' }}
                        value={selectedExisting} onChange={e=>setSelectedExisting(e.target.value)} required>
                        <option value="">Select existing unit…</option>
                        {existingUnits.map(u=><option key={u.unit_id} value={u.unit_id}>{u.unit_type} ({u.unit_category})</option>)}
                      </select>
                      <svg style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', width:16, height:16, color:'#94a3b8', pointerEvents:'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                    </div>
                  </Field>

                  <Field label={`How many ${compUnit?.unit_type||'selected unit'} are in ${newUnitType||'new unit'}? *`}>
                    <input className="v2-input" type="number" placeholder="e.g. 24" value={convRate} onChange={e=>setConvRate(e.target.value)} min="0" step="0.01" required />
                  </Field>

                  <ProfitBox orderPrice={unitCategory==='buying'?orderPrice:''} retailPrice={unitCategory==='buying'?retailPrice:retailPrice} convRate={convRate} isAddingNew={true} unitCategory={unitCategory} newUnitType={newUnitType} compUnit={compUnit?.unit_type} />
                </div>
              )}

              <BtnPrimary type="submit" disabled={disabled} style={{ fontSize:15, width:'100%', padding:14 }}>
                {disabled ? 'Saving…' : 'Add Unit'}
              </BtnPrimary>
            </>
          )}
        </div>
      </form>

      {/* Profit margin warning */}
      <Modal open={profitWarnOpen} onClose={() => setProfitWarnOpen(false)} title="Profit Margin Warning"
        footer={<>
          <BtnSecondary onClick={() => setProfitWarnOpen(false)}>Update Values</BtnSecondary>
          <BtnPrimary onClick={async()=>{ setProfitWarnOpen(false); await submitForm(pendingData); }}>Proceed Anyway</BtnPrimary>
        </>}>
        <p style={{ fontSize:14, color:'#475569', lineHeight:1.6, margin:0 }}>
          {calcMargin()<0
            ? `The pricing results in a loss of ${Math.abs((calcMargin()||0)*100).toFixed(1)}% per unit sold.`
            : `The profit margin of ${((calcMargin()||0)*100).toFixed(1)}% seems unusually high.`
          } Would you like to proceed or update the values?
        </p>
      </Modal>

      {/* Add another unit dialog (from product flow) */}
      <Modal open={addAnotherOpen} onClose={() => setAddAnotherOpen(false)} title="Add Additional Units?"
        footer={<>
          <BtnSecondary onClick={() => { setAddAnotherOpen(false); navigate(`/new/dashboard/inventories/add?product_id=${productId}`); }}>No, Go to Stock Entry</BtnSecondary>
          <BtnPrimary onClick={() => { setAddAnotherOpen(false); setDisabled(false); setBuyUnit(''); setSellUnit(''); setNewUnitType(''); setSelectedExisting(''); setConvRate(''); setOrderPrice(''); setRetailPrice(''); setUnitCategory('buying'); setSelectedSupplierIds([]); setPrimarySupplierId(''); setUnitTypeError(''); }}>Yes, Add More Units</BtnPrimary>
        </>}>
        <p style={{ fontSize:14, color:'#475569', lineHeight:1.6, margin:0 }}>Would you like to add additional buying or selling units to this product?</p>
      </Modal>

      {/* Add supplier redirect prompt */}
      <Modal open={addSupplierOpen} onClose={() => setAddSupplierOpen(false)} title="Add New Supplier"
        footer={<><BtnSecondary onClick={() => setAddSupplierOpen(false)}>Cancel</BtnSecondary><BtnPrimary onClick={handleSaveAndAddSupplier}>Go to Add Supplier</BtnPrimary></>}>
        <p style={{ fontSize:14, color:'#475569', lineHeight:1.6, margin:0 }}>You'll be taken to the Add Supplier page. Your current form progress will be saved and restored when you return.</p>
      </Modal>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
